import { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import { useQuery } from '@apollo/client'
import { useSites } from 'store'
import { useSearchParams } from 'router'
import EmptyScreen from 'packages/ui/EmptyScreen'
import Select from 'components/global/CUK/AsyncSelect/Select'
import useTopology from './use-topology'
import { TopologyWrapper, TreeArea, ExpandButton } from './Styled'
import TopologyTree from './Tree'
import TopologySidebar from './Sidebar'
import TopologyCaption from './Caption'
import TopologyDetailCard from './CardDetail'
import TopologyLoading from './LoadingSkeleton'
import { HOTSPOTS_BY_SITE } from './operations-gql/queries'

const inputBorder = '1px solid #EFEFEF'
const inputRadius = '6px'
const inputHeight = 32

const TopologyView = ({
  searchQuery,
  onExpandChange,
  treeRef,
  treeData,
  equipments,
  existingTypes,
  hiddenTypes,
  toggleType,
  loading,
  error,
  selectedEquipment,
  hoveredEquipment,
  isExpanded,
  handleSelectNode,
  handleCloseDetail,
  handleHoverNode,
  handleUnhoverNode,
  handleSelectFromSidebar,
  toggleExpand,
  getHiddenChildren,
  revealChildrenOf,
  hideRevealedChildrenOf,
  revealedParentMacs,
}) => {
  const { t } = useTranslation()

  const handleToggleExpand = useCallback(() => {
    toggleExpand()
    onExpandChange?.(!isExpanded)
  }, [toggleExpand, onExpandChange, isExpanded])

  if (loading) {
    return <TopologyLoading />
  }

  if (error) {
    return (
      <TopologyWrapper>
        <EmptyScreen icon="wifi">
          {t('supervision.topology.error')}
        </EmptyScreen>
      </TopologyWrapper>
    )
  }

  if (!treeData) {
    return (
      <TopologyWrapper>
        <EmptyScreen icon="network-wired">
          {t('supervision.topology.missing')}
        </EmptyScreen>
      </TopologyWrapper>
    )
  }

  return (
    <TopologyWrapper>
      <TopologySidebar
        equipments={equipments}
        existingTypes={existingTypes}
        selectedMac={selectedEquipment?.mac_address}
        onSelectEquipment={handleSelectFromSidebar}
        isExpanded={isExpanded}
        searchQuery={searchQuery}
      />

      <TreeArea>
        <TopologyTree
          ref={treeRef}
          treeData={treeData}
          selectedMac={selectedEquipment?.mac_address}
          onSelectNode={handleSelectNode}
          onHoverNode={handleHoverNode}
          onUnhoverNode={handleUnhoverNode}
          onRevealChildren={revealChildrenOf}
          onHideRevealedChildren={hideRevealedChildrenOf}
          revealedParentMacs={revealedParentMacs}
        />

        <ExpandButton onClick={handleToggleExpand}>
          <i
            className={`fa-regular ${isExpanded
              ? 'fa-arrow-down-left-and-arrow-up-right-to-center'
              : 'fa-arrow-up-right-and-arrow-down-left-from-center'}`}
            style={{ fontSize: 13 }}
          />
        </ExpandButton>

        <TopologyCaption
          existingTypes={existingTypes}
          hiddenTypes={hiddenTypes}
          onToggleType={toggleType}
        />

        <TopologyDetailCard
          equipment={hoveredEquipment || selectedEquipment}
          hiddenChildren={getHiddenChildren((hoveredEquipment || selectedEquipment)?.mac_address)}
          onClose={handleCloseDetail}
        />
      </TreeArea>
    </TopologyWrapper>
  )
}

const TopologyPage = ({ sites = [] }) => {
  const { t } = useTranslation()
  const { focusId } = useParams()
  const [searchParams] = useSearchParams()
  const [{ selected }] = useSites() 

  const [searchQuery, setSearchQuery] = useState('')
  const [selectedHotspotId, setSelectedHotspotId] = useState(null)
  const [isTopologyExpanded, setIsTopologyExpanded] = useState(false)

  const handleExpandChange = useCallback((expanded) => {
    setIsTopologyExpanded(expanded)
  }, [])

  const site = useMemo(() => {
    if (focusId) return sites.find(s => s.sf_id === focusId)
    if (selected.length === 1) return sites.find(s => s.sf_id === selected[0].sf_id)
    return null
  }, [sites, focusId, selected])

  const { data: hotspotsData, loading: hotspotsLoading } = useQuery(HOTSPOTS_BY_SITE, {
    variables: { sf_id: site?.sf_id },
    skip: !site?.sf_id,
  })

  const hotspots = hotspotsData?.hotspots || []

  const hotspotId = useMemo(() => {
    if (selectedHotspotId) return selectedHotspotId
    return hotspots[0]?.id || null
  }, [selectedHotspotId, hotspots])

  const hotspotOptions = useMemo(() => {
    const seen = new Set()
    return hotspots
      .filter(h => { if (seen.has(String(h.id))) return false; seen.add(String(h.id)); return true })
      .map(h => ({ id: h.id, name: h.name }))
  }, [hotspots])
  const hotspotValue = hotspotOptions.find(o => String(o.id) === String(hotspotId)) || hotspotOptions[0] || null

  const topology = useTopology(site?.sf_id, hotspotId)
  const focusEquipmentMac = searchParams.get('equipmentMac')

  useEffect(() => {
    if (!focusEquipmentMac || topology.loading || !topology.equipments.length) return
    topology.focusEquipmentByMac(focusEquipmentMac)
  }, [focusEquipmentMac, topology.loading, topology.equipments.length, topology.focusEquipmentByMac])

  if (!site) {
    return (
      <TopologyWrapper>
        <EmptyScreen icon="network-wired">
          {t('supervision.topology.no_site')}
        </EmptyScreen>
      </TopologyWrapper>
    )
  }

  if (hotspotsLoading) {
    return <TopologyLoading />
  }

  const showSearch = topology.treeData !== null && !isTopologyExpanded
  const showSelect = hotspotOptions.length > 1

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', overflow: 'hidden' }}>
      {(showSearch || showSelect) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: showSearch ? 2 : 1, pr: 2, pt: '6px', pb: '12px', flexShrink: 0 }}>
          {showSearch && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.75,
              border: inputBorder, borderRadius: inputRadius,
              px: 1.25, height: inputHeight,
              minWidth: 180, maxWidth: 260, flex: '0 1 220px',
              '&:focus-within': { borderColor: '#2570EB' },
            }}>
              <i className="fa-regular fa-magnifying-glass" style={{ color: '#656565', fontSize: 12, flexShrink: 0 }} />
              <Box
                component="input"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder={t('supervision.topology.sidebar.search_placeholder')}
                sx={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 12, color: '#292929',
                  '&::placeholder': { color: '#9CA3AF' },
                }}
              />
              {searchQuery && (
                <Box
                  component="button"
                  onClick={() => setSearchQuery('')}
                  sx={{ border: 'none', background: 'none', cursor: 'pointer', p: 0, lineHeight: 1, color: '#9CA3AF', '&:hover': { color: '#464646' } }}
                >
                  <i className="fa-regular fa-xmark" style={{ fontSize: 10 }} />
                </Box>
              )}
            </Box>
          )}

          {showSelect && (
            <Box sx={{
              width: 320, flexShrink: 0,
              '& [class$="-control"]': {
                border: `${inputBorder} !important`,
                boxShadow: 'none !important',
                minHeight: `${inputHeight}px !important`,
                height: `${inputHeight}px !important`,
                borderRadius: `${inputRadius} !important`,
                backgroundColor: 'transparent !important',
              },
              '& [class$="-indicatorSeparator"]': { display: 'none' },
            }}>
              <Select
                value={hotspotValue}
                onChange={val => val && setSelectedHotspotId(val.id)}
                options={hotspotOptions}
                keyField="id"
                mainField="name"
                height={inputHeight}
                menuWidth={320}
                menuPortalTarget={document.body}
                isClearable={false}
                hideSelectedOptions
                components={{
                  Input: () => null,
                  ValueContainer: ({ ...props }) => {
                    const val = props.selectProps.value
                    const mainField = props.selectProps.mainField || 'name'
                    return (
                      <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', px: 1.5, display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{
                          fontSize: 12, color: val ? '#464646' : '#9CA3AF',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                        }}>
                          {val ? val[mainField] : props.selectProps.placeholder}
                        </Box>
                      </Box>
                    )
                  },
                }}
              />
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <TopologyView {...topology} searchQuery={searchQuery} onExpandChange={handleExpandChange} />
      </Box>
    </Box>
  )
}

export default TopologyPage
